/**
=========================================================
* SEO — reusable head manager
=========================================================
* Sets per-page <title>, meta description, canonical URL, Open Graph and
* Twitter Card tags, robots directives and optional JSON-LD structured data.
* Powered by react-helmet-async (HelmetProvider is mounted in src/index.js).
*/

import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";

import siteConfig from "config/site";

function SEO({ title, description, path, image, type, noindex, schema, imageAlt }) {
  const metaDescription = description || siteConfig.defaultDescription;

  // Full <title>: use the template unless a page opts out with an absolute title.
  const fullTitle = title ? siteConfig.titleTemplate.replace("%s", title) : siteConfig.defaultTitle;

  const canonical = `${siteConfig.siteUrl}${path || "/"}`.replace(/([^:]\/)\/+/g, "$1");
  const ogImage = image || siteConfig.socialImage;

  // Normalise JSON-LD into an array so pages can pass one or many schemas.
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteConfig.siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAlt || siteConfig.siteName} />
      <meta property="og:locale" content={siteConfig.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt || siteConfig.siteName} />

      {/* Structured data */}
      {schemas.map((entry, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}

SEO.defaultProps = {
  title: "",
  description: "",
  path: "/",
  image: "",
  imageAlt: "",
  type: "website",
  noindex: false,
  schema: null,
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  type: PropTypes.string,
  noindex: PropTypes.bool,
  schema: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default SEO;
